import { Navigate, createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { AtlasPageView } from '@/components/Atlas/AtlasPageView';
import { RequireFeatureFlag } from '@/components/Auth/RequireFeatureFlag';
import { DocsSidebar } from '@/components/CodebaseView/DocsSidebar';
import { DocumentSetSkeletonLoader } from '@/components/CodebaseView/DocumentSet/DocumentSetSkeletonLoader';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { TocProvider, useToc } from '@/contexts/TocContext';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { searchParam } from '@/utils/router';

const atlasPageSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/atlas/$')({
  component: GatedAtlasDetailPage,
  validateSearch: zodValidator(atlasPageSearchSchema),
});

// Gate atlas detail pages behind the SERVE_ATLAS flag. When the flag is loaded-and-off,
// redirect to this codebase's overview; while loading, show the same skeleton AtlasPageView
// shows for a pending page (avoids flashing a redirect before /me resolves). See RequireFeatureFlag.
function GatedAtlasDetailPage() {
  const { codebaseId, organizationName } = useCodebaseRoute();
  return (
    <RequireFeatureFlag
      flag={FEATURE_FLAGS.SERVE_ATLAS}
      loading={<DocumentSetSkeletonLoader />}
      redirect={
        <Navigate to="/$organizationName/cb/$codebaseId/overview/$" replace params={{ organizationName, codebaseId }} />
      }
    >
      <ProvidedAtlasDetailPage />
    </RequireFeatureFlag>
  );
}

function ProvidedAtlasDetailPage() {
  // The splat is the atlas page route verbatim (it contains slashes, e.g. `sections/012-slug.md`).
  const { _splat: pageRoute } = Route.useParams();

  return (
    // Key the TOC to the page route so it resets when navigating between atlas pages.
    <TocProvider key={pageRoute}>
      <div className="mx-auto flex w-full flex-col gap-y-6 pt-4 pb-4">
        <AtlasDetailPage pageRoute={pageRoute ?? ''} />
      </div>
    </TocProvider>
  );
}

interface AtlasDetailPageProps {
  pageRoute: string;
}

function AtlasDetailPage({ pageRoute }: AtlasDetailPageProps) {
  const { codebaseId, organizationName } = useCodebaseRoute();
  const { branchId, versionId } = Route.useSearch();
  const { editor, handleTabClick, tocItems } = useToc();

  return (
    <>
      <LeftSidebarPortal.Mount>
        <DocsSidebar
          backTo="/$organizationName/cb/$codebaseId/atlas"
          backParams={{
            organizationName,
            codebaseId,
          }}
          backSearch={
            branchId || versionId
              ? { ...(branchId ? { branchId } : {}), ...(versionId ? { versionId } : {}) }
              : undefined
          }
          tocItems={tocItems}
          onTocClick={handleTabClick}
          editor={editor}
          showExpandCollapseButton={false}
        />
      </LeftSidebarPortal.Mount>
      <AtlasPageView route={pageRoute} />
    </>
  );
}
